/**
 * Handle DocuSign's return hop on whichever dashboard the tenant is on
 * (COL-533).
 *
 * DocuSign sends the tenant to `${site}/dashboard?tab=contract&docusign=complete`
 * and appends its own `event` — `signing_complete` when they finished, or
 * `ttl_expired` when the signing session timed out. Until now only V1 could
 * read that: `DashboardRoute` latched any `docusign`/`event` param to V1,
 * because V1 was the only surface that knew what to do with it. So a tenant
 * who signed from the new dashboard was dropped onto the old one — which is
 * exactly what the V2 copy had been promising would not happen.
 *
 * The work itself is small and worth stating, because "just route it to V2"
 * would lose it:
 *
 *  - the envelope must be SYNCED. DocuSign redirects the browser the moment
 *    the tenant finishes; our row is updated by the webhook, which may not
 *    have landed yet. Without this call the dashboard shows "ready to sign"
 *    to someone who has just signed.
 *  - the params must be STRIPPED, or a refresh re-runs the sync and
 *    re-announces a signature from minutes ago.
 *  - stripping must wait for a booking id. V1 returns early without clearing
 *    the URL when the booking is still loading, so the effect can run again
 *    once it arrives; dropping that makes the sync silently never happen on
 *    a slow load.
 *
 * Written to be shared by both dashboards — this is the hop that decides
 * whether a signed contract is recorded as signed, so one implementation is
 * the goal. V1 does NOT use it yet: V1's effect also drives its tab selection
 * and a persistent expired-session notice with no V2 equivalent, so adopting
 * this hook there means rewriting V1's signing path. Until that happens the
 * two CAN drift, and that is a known, flagged risk rather than a solved one.
 */

import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { syncDocusignEnvelope } from "@/lib/docusign-contract";

export type DocusignReturnLabels = {
  /** Shown when the sync confirms the contract is now signed. */
  signed: string;
  /** The signature is safe in DocuSign; only our mirror failed. */
  syncFailed: string;
  /** DocuSign's session timed out before they finished. */
  sessionExpired: string;
};

type Options = {
  /** The tenant's active booking, or null while it loads. */
  bookingId: number | null;
  labels: DocusignReturnLabels;
  /** Toasts, injected so this module does not pick a UI library. */
  notify: {
    success: (message: string) => void;
    info: (message: string) => void;
    error: (message: string) => void;
  };
};

export function useDocusignSigningReturn({
  bookingId,
  labels,
  notify,
}: Options): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  /*
    One handling per return, per mount.

    The effect depends on searchParams, and the sync's own
    invalidate/strip both change state — without this latch a slow sync can
    be started twice and the tenant sees the signed toast twice.
  */
  const handled = useRef(false);

  /*
    Always strip through the LATEST setSearchParams, never the one captured
    when the sync started.

    react-router-dom hands a functional updater the RENDER-TIME params held
    in its own useCallback closure (6.30.6, dist/index.js:1031) and then
    calls `navigate("?" + next)`, replacing the whole query string. So a
    stale setSearchParams rewinds the URL to the moment it was captured: a
    tenant who switches tabs while the envelope is syncing gets yanked back
    to tab=contract a second later. Reading the ref means the updater sees
    the CURRENT params and only removes the three keys it owns.
  */
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  });

  /*
    The sync outlives the component if the tenant navigates away (including
    the ?dashboard=v1 hand-off, where V1 then runs its own handler). Nothing
    should be announced or rewritten from a dead mount.
  */
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  const docusign = searchParams.get("docusign");
  const event = searchParams.get("event");
  const bookingIdParam = searchParams.get("bookingId");

  useEffect(() => {
    if (docusign !== "complete" && !event) return;
    if (handled.current) return;

    const stripParams = () => {
      if (!alive.current) return;
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("docusign");
          next.delete("event");
          next.delete("bookingId");
          return next;
        },
        { replace: true }
      );
    };

    if (event === "ttl_expired") {
      handled.current = true;
      notify.info(labels.sessionExpired);
      stripParams();
      return;
    }

    if (event === "signing_complete") {
      // Prefer the id DocuSign echoed back: the tenant may have more than
      // one booking, and the signed envelope belongs to a specific one.
      const parsed = bookingIdParam ? parseInt(bookingIdParam, 10) : NaN;
      const id = Number.isFinite(parsed) && parsed > 0 ? parsed : bookingId;
      /*
        No id yet — the booking query is still in flight. Return WITHOUT
        stripping so this runs again when it resolves. Clearing here would
        drop the signature on the floor for anyone on a slow connection.
      */
      if (!id) return;

      handled.current = true;
      syncDocusignEnvelope(id, labels.syncFailed)
        .then((res: any) => {
          // The invalidation is safe either way -- it refreshes a cache, it
          // does not touch the URL -- but the toast must not fire from a
          // mount the tenant has already left.
          queryClient.invalidateQueries({ queryKey: ["active-booking"] });
          if (res?.updated && alive.current) notify.success(labels.signed);
        })
        .catch((e: any) => {
          // The signature is recorded in DocuSign regardless; only our copy
          // of the status failed, and the message says so.
          const detail = [e?.message, e?.details].filter(Boolean).join(" — ");
          if (alive.current) notify.error(detail || labels.syncFailed);
          queryClient.invalidateQueries({ queryKey: ["active-booking"] });
        })
        .finally(stripParams);
      return;
    }

    // `docusign=complete` with no event: nothing to reconcile, just tidy up.
    handled.current = true;
    stripParams();
    // `setSearchParams` is deliberately NOT a dependency: it changes on every
    // navigation, and the hook reads it through a ref precisely so a change
    // does not re-run (or stale-capture) an in-flight return.
  }, [docusign, event, bookingIdParam, bookingId, labels, notify, queryClient]);
}
